import { createTheme, type MantineColorsTuple } from '@mantine/core';

/**
 * Tema de Mantine cableado a los tokens de marca de Tribbu (`styles/tribbu-tokens.css`, vendorizado
 * de tribbu-product/brand-kit vía la skill data-slides-creator).
 *
 * Los tokens son la fuente canónica: aquí NO se define ningún color nuevo.
 *
 * Mantine exige rampas de 10 pasos y la rampa Rosa BB tiene 8 (000/100/200/400/600/800/900/1000).
 * En vez de inventar dos tintes intermedios —que serían colores fuera de marca— se repiten los
 * extremos. Con `primaryShade: 6` el color primario cae en p800 (#965392), que es exactamente
 * `--tribbu-accent`: el valor que la marca reserva para texto y marcas sobre fondo CLARO, porque
 * Rosa BB puro tiene ~1:1 de contraste sobre blanco y sería ilegible.
 */
const rosaBB: MantineColorsTuple = [
  '#fff8fe', // p000 (repetido en 0 y 1: la rampa no tiene un paso mas claro)
  '#fff8fe',
  '#ffebfc', // p100
  '#ffd6f8', // p200 — Rosa BB
  '#f9b5ed', // p400
  '#db95d5', // p600
  '#965392', // p800 — --tribbu-accent, el shade primario
  '#4e214b', // p900
  '#391a3a', // p1000
  '#391a3a',
];

/**
 * La capa SEMANTICA, sobre las rampas de marca.
 *
 * Hasta la auditoria de diseño (2026-08-11) los componentes usaban `color="red"`, `"orange"` y
 * `"green"` de la paleta POR DEFECTO de Mantine, mientras las barras de `Distribucion` usaban
 * `--color-red-r800` y `--color-orange-o800` de los tokens. O sea: dos rojos y dos naranjas
 * distintos, para el mismo significado, en la misma pantalla. Ops lo nota.
 *
 * Se arregla en la RAIZ y no uso por uso: sobreescribiendo los nombres de Mantine con las rampas de
 * marca, los 14 `color="red|orange|green"` que ya hay en los componentes pasan a marca sin tocarlos,
 * y cualquiera que se escriba mañana tambien.
 *
 * Mismo apaño que en `rosaBB` y por el mismo motivo: las rampas de marca tienen 8 pasos
 * (000/100/200/400/600/800/900/1000) y Mantine exige 10, asi que se repiten los extremos en vez de
 * inventar tintes fuera de marca. El paso 800 —el que la marca reserva para texto y marcas sobre
 * fondo claro— cae en el indice 6, que es el `primaryShade` y el que Mantine usa por defecto para
 * `Alert`, `Badge` y `Button` con color explicito.
 *
 * `gray` se deja como esta a proposito: Mantine lo usa internamente para bordes, fondos y texto
 * atenuado en decenas de componentes, y sobreescribirlo es un cambio de superficie mucho mayor que
 * lo que arregla. Los dos `color="gray"` que hay son alertas informativas.
 */
const rojoBB: MantineColorsTuple = [
  '#ffebeb', // r000
  '#ffebeb',
  '#ffc3c3', // r100
  '#ff9a9a', // r200
  '#ff7373', // r400
  '#f04c4c', // r600
  '#d42020', // r800 — el shade que usan Alert/Badge/Button
  '#790c0c', // r900
  '#390909', // r1000
  '#390909',
];

const naranjaBB: MantineColorsTuple = [
  '#fff5e4', // o000
  '#fff5e4',
  '#ffdfa7', // o100
  '#ffcc73', // o200
  '#feb83e', // o400
  '#f7a61b', // o600
  '#d38908', // o800
  '#a16905', // o900
  '#482c02', // o1000
  '#482c02',
];

const verdeBB: MantineColorsTuple = [
  '#e8ffea', // g000
  '#e8ffea',
  '#9bffa5', // g100
  '#72f17f', // g200
  '#49ea5a', // g400
  '#2ed440', // g600
  '#19aa29', // g800
  '#0b7316', // g900
  '#103711', // g1000
  '#103711',
];

export const theme = createTheme({
  colors: { rosaBB, red: rojoBB, orange: naranjaBB, green: verdeBB },
  primaryColor: 'rosaBB',
  primaryShade: 6,

  // Degular Bold solo existe en peso 700 y la marca lo reserva a titulares. El cuerpo va en
  // DM Sans, AUTOALOJADA desde 2026-08-11 (fuente variable, @font-face en styles/app.css): hasta
  // entonces los tokens la declaraban pero el fichero no se cargaba, asi que todo caia a system-ui
  // y la identidad visual no se aplicaba de verdad.
  fontFamily: 'var(--tribbu-font-body)',
  headings: { fontFamily: 'var(--tribbu-font-display)', fontWeight: '700' },

  defaultRadius: 'md',
  cursorType: 'pointer',
});
